import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useVisitorDetail, useAddNote, useUpdateStatus, useSetReminder } from '../../hooks/useMarketing';
import marketingService from '../../../services/api/marketingService';
import OutreachModal from '../Pipeline/OutreachModal';

/**
 * The logo the visitor uploaded, shown and downloadable.
 *
 * Fetched as a blob through the authenticated API rather than linked: the
 * stored blob URL carries a SAS token that expires a year after upload, and
 * an <img> tag can't send our auth header. The same blob backs the preview
 * and the download, so the file is only pulled once.
 */
function LogoPreview({ applicationId, fileName }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!applicationId) return undefined;
    let url = null;
    let cancelled = false;
    marketingService.downloadTrademarkImage(applicationId)
      .then((data) => {
        if (cancelled) return;
        url = window.URL.createObjectURL(data);
        setBlob(data);
        setObjectUrl(url);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.status === 404 ? 'No logo stored' : 'Could not load the logo');
      });
    return () => {
      cancelled = true;
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [applicationId]);

  const handleDownload = () => {
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'trademark-logo.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ marginTop: '14px', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
      <span className="mkt-field-label">Logo</span>
      {error && <p style={{ color: '#94a3b8', fontSize: '13px', margin: '6px 0 0' }}>{error}</p>}
      {!error && !objectUrl && (
        <p style={{ color: '#94a3b8', fontSize: '13px', margin: '6px 0 0' }}>Loading logo...</p>
      )}
      {objectUrl && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginTop: '8px', flexWrap: 'wrap' }}>
          <a
            href={objectUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open full size"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '140px', height: '140px', padding: '8px', background: 'white',
              border: '1px solid #e2e8f0', borderRadius: '10px',
            }}
          >
            <img
              src={objectUrl}
              alt={fileName || 'Uploaded logo'}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </a>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {fileName && (
              <span style={{ fontSize: '12px', color: '#64748b', wordBreak: 'break-all' }}>{fileName}</span>
            )}
            <button className="mkt-btn mkt-btn-secondary" onClick={handleDownload}>
              Download logo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Mirrors the pipeline's report-first column order. `first_contact` is a
// legacy value (bucketed into Report sent on the board) — kept here so old
// records still render a labelled option.
const STATUS_OPTIONS = [
  { value: 'not_contacted', label: 'Awaiting Report' },
  { value: 'first_contact', label: 'First Contact (legacy)' },
  { value: 'report_sent', label: 'Report Sent' },
  { value: 'checkout_nudge', label: 'First Nudge' },
  { value: 'second_contact', label: 'Second Nudge' },
  { value: 'call', label: 'Call List' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'responded', label: 'Responded' },
  { value: 'paid', label: 'Paid' },
  { value: 'do_not_contact', label: 'Do Not Contact' },
  { value: 'not_serious', label: 'Not Serious' },
];

const TIMELINE_ICONS = {
  visitor_landed: '🔗',
  tracking_captured: '🔗',
  wizard_started: '🚀',
  step_start: '▶️',
  step_business: '🏢',
  step_analysis: '🔍',
  step_contact: '📧',
  step_countries: '🌍',
  step_details: '📋',
  step_checkout: '💳',
  analysis_triggered: '🔍',
  analysis_completed: '✅',
  contact_captured: '📧',
  countries_selected: '🌍',
  payment_complete: '💰',
  ai_cost: '🤖',
  note_added: '📝',
  follow_up_sent: '📤',
  status_change: '🔄',
};

function VisitorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: visitor, isLoading, error } = useVisitorDetail(id);
  const addNote = useAddNote();
  const updateStatus = useUpdateStatus();
  const setReminder = useSetReminder();

  const [noteText, setNoteText] = useState('');
  const [outreachOpen, setOutreachOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="mkt-profile-loading">
        <div className="mkt-loading-spinner" />
        <p>Loading visitor profile...</p>
      </div>
    );
  }

  if (error || !visitor) {
    return (
      <div className="mkt-profile-error">
        <p>Visitor not found</p>
        <button onClick={() => navigate('/marketing/pipeline')} className="mkt-btn mkt-btn-secondary">
          Back to Pipeline
        </button>
      </div>
    );
  }

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    await addNote.mutateAsync({ visitorId: id, content: noteText.trim() });
    setNoteText('');
  };

  const handleStatusChange = async (status) => {
    await updateStatus.mutateAsync({ visitorId: id, status });
  };

  // The compose modal reads flat fields (like pipeline cards) — the detail
  // endpoint nests analysis, so flatten what the composer's crib panel uses.
  const composeVisitor = {
    ...visitor,
    riskLevel: visitor.riskLevel || visitor.analysis?.riskLevel || null,
    analysisSummary: visitor.analysisSummary || visitor.analysis?.summary || null,
  };

  return (
    <div className="mkt-profile">
      {/* Back button */}
      <button
        onClick={() => navigate('/marketing/pipeline')}
        className="mkt-btn mkt-btn-back"
      >
        ← Back to Pipeline
      </button>

      {/* Header */}
      <div className="mkt-profile-header">
        <div className="mkt-profile-identity">
          <h2 className="mkt-profile-name">
            {visitor.name || visitor.browseId?.substring(0, 16) || visitor.tempUserId?.substring(0, 16)}
          </h2>
          {visitor.email && <p className="mkt-profile-email">{visitor.email}</p>}
          {visitor.phone && <p className="mkt-profile-phone">{visitor.phone}</p>}
          {visitor.isReturning && (
            <span className="mkt-badge mkt-badge-returning">Returning Visitor</span>
          )}
        </div>
        <div className="mkt-profile-stage">
          <span className={`mkt-stage-badge mkt-stage-${visitor.currentStep || 'landing'}`}>
            {formatStage(visitor.currentStep || 'landing')}
          </span>
          {visitor.wizardMode && (
            <span className="mkt-visitor-mode" style={{marginLeft: 8}}>{visitor.wizardMode}</span>
          )}
        </div>
      </div>

      {/* Actions — up top so composing is one click */}
      <div className="mkt-profile-section mkt-profile-actions">
        <div className="mkt-action-buttons">
          {visitor.email && (
            <button
              onClick={() => setOutreachOpen(true)}
              className="mkt-btn mkt-btn-primary"
            >
              ✉️ Email {(visitor.name || '').split(' ')[0] || 'visitor'}
            </button>
          )}
          <select
            className="mkt-status-select"
            value={visitor.contactStatus || visitor.followUpStatus || 'not_contacted'}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={updateStatus.isPending}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span style={{ fontSize: '12px', color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            Remind me in:
            {[1, 3, 7].map((d) => (
              <button
                key={d}
                className="mkt-btn mkt-btn-ghost"
                style={{ fontSize: '12px', padding: '2px 8px' }}
                disabled={setReminder.isPending}
                onClick={() => setReminder.mutate({ visitorId: id, days: d })}
              >
                {d}d
              </button>
            ))}
            {visitor.nextTouchAt && (
              <>
                <span style={{ color: '#b45309' }}>
                  ⏰ {new Date(visitor.nextTouchAt).toLocaleDateString('en-AU')}
                </span>
                <button
                  className="mkt-btn mkt-btn-ghost"
                  style={{ fontSize: '12px', padding: '2px 8px' }}
                  onClick={() => setReminder.mutate({ visitorId: id, days: 0 })}
                >
                  clear
                </button>
              </>
            )}
          </span>
          {visitor.clarityUrl && (
            <a
              href={visitor.clarityUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mkt-btn mkt-btn-secondary"
            >
              👁 Watch Session
            </a>
          )}
        </div>
      </div>

      {/* Trademark */}
      {visitor.trademarkName && (
        <div className="mkt-profile-section">
          <h3>Trademark</h3>
          <div className="mkt-profile-grid">
            <div className="mkt-profile-field">
              <span className="mkt-field-label">Name</span>
              <span className="mkt-field-value">&ldquo;{visitor.trademarkName}&rdquo;</span>
            </div>
            <div className="mkt-profile-field">
              <span className="mkt-field-label">Type</span>
              <span className="mkt-field-value">{visitor.trademarkType || 'word'}</span>
            </div>
            {visitor.selectedCountries?.length > 0 && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Countries</span>
                <span className="mkt-field-value">{visitor.selectedCountries.join(', ')}</span>
              </div>
            )}
            {visitor.classCount > 0 && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Classes</span>
                <span className="mkt-field-value">{visitor.classCount}</span>
              </div>
            )}
            {visitor.cartValue > 0 && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Cart Value</span>
                <span className="mkt-field-value mkt-value-highlight">${visitor.cartValue.toLocaleString()}</span>
              </div>
            )}
            {visitor.transactionValue > 0 && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Paid</span>
                <span className="mkt-field-value mkt-value-highlight">${visitor.transactionValue.toLocaleString()} {visitor.currency || ''}</span>
              </div>
            )}
          </div>
          {visitor.hasTrademarkImage && (
            <LogoPreview
              applicationId={visitor.applicationId}
              fileName={visitor.trademarkFileName}
            />
          )}
        </div>
      )}

      {/* Business Info */}
      {(visitor.businessName || visitor.businessDescription || visitor.industry || visitor.websiteUrl || visitor.intendedUse) && (
        <div className="mkt-profile-section">
          <h3>Business Information</h3>
          <div className="mkt-profile-grid">
            {visitor.businessName && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Business Name</span>
                <span className="mkt-field-value">{visitor.businessName}</span>
              </div>
            )}
            {visitor.industry && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Industry</span>
                <span className="mkt-field-value">{visitor.industry}</span>
              </div>
            )}
            {visitor.websiteUrl && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Website</span>
                <span className="mkt-field-value">
                  <a href={visitor.websiteUrl.startsWith('http') ? visitor.websiteUrl : `https://${visitor.websiteUrl}`} target="_blank" rel="noopener noreferrer">{visitor.websiteUrl}</a>
                </span>
              </div>
            )}
            {visitor.intendedUse && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Intended Use</span>
                <span className="mkt-field-value">{visitor.intendedUse}</span>
              </div>
            )}
            {visitor.businessDescription && (
              <div className="mkt-profile-field mkt-field-wide">
                <span className="mkt-field-label">Description</span>
                <span className="mkt-field-value">{visitor.businessDescription}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Previous Sessions (returning visitor) */}
      {visitor.previousSessions?.length > 0 && (
        <div className="mkt-profile-section">
          <h3>Previous Sessions</h3>
          <div className="mkt-funnel-visitors-list">
            {visitor.previousSessions.map((s) => (
              <div key={s.id} className="mkt-funnel-visitor-row">
                <div className="mkt-funnel-visitor-info">
                  <span className="mkt-visitor-name">
                    {s.trademarkName || s.currentStep}
                  </span>
                  <span className="mkt-visitor-date">
                    {formatDate(s.createdAt)}
                  </span>
                  <span className="mkt-visitor-mode">{s.currentStep}</span>
                </div>
                <div className="mkt-funnel-visitor-actions">
                  {s.clarityUrl && (
                    <a href={s.clarityUrl} target="_blank" rel="noopener noreferrer" className="mkt-btn-icon" title="Clarity">
                      👁
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attribution — analytics detail, collapsed by default */}
      {visitor.attribution && (visitor.attribution.trafficSource || visitor.attribution.utmSource || visitor.attribution.gclid) && (
        <details className="mkt-profile-section">
          <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#475569', fontSize: '14px' }}>
            Marketing details (source, campaign, tracking)
          </summary>
          <div className="mkt-profile-grid" style={{ marginTop: '10px' }}>
            {visitor.attribution.trafficSource && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Traffic Source</span>
                <span className="mkt-field-value">{visitor.attribution.trafficSource}</span>
              </div>
            )}
            {visitor.attribution.utmSource && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Source / Medium</span>
                <span className="mkt-field-value">
                  {visitor.attribution.utmSource}{visitor.attribution.utmMedium ? ` / ${visitor.attribution.utmMedium}` : ''}
                </span>
              </div>
            )}
            {(visitor.attribution.adCampaign || visitor.attribution.utmCampaign) && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Campaign</span>
                <span className="mkt-field-value">{visitor.attribution.adCampaign || visitor.attribution.utmCampaign}</span>
              </div>
            )}
            {visitor.attribution.adGroup && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Ad Group</span>
                <span className="mkt-field-value">{visitor.attribution.adGroup}</span>
              </div>
            )}
            {visitor.attribution.adName && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Ad</span>
                <span className="mkt-field-value">{visitor.attribution.adName}</span>
              </div>
            )}
            {(visitor.attribution.adKeyword || visitor.attribution.utmTerm) && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Keyword</span>
                <span className="mkt-field-value">
                  {visitor.attribution.adKeyword || visitor.attribution.utmTerm}
                  {visitor.attribution.adMatchType ? ` (${String(visitor.attribution.adMatchType).toLowerCase()} match)` : ''}
                </span>
              </div>
            )}
            {visitor.attribution.gclid && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Google Click ID</span>
                <span className="mkt-field-value mkt-field-mono">{visitor.attribution.gclid.substring(0, 20)}...</span>
              </div>
            )}
            {visitor.attribution.landingPage && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Landing Page</span>
                <span className="mkt-field-value">{visitor.attribution.landingPage}</span>
              </div>
            )}
            {visitor.attribution.referrer && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Referrer</span>
                <span className="mkt-field-value">{visitor.attribution.referrer}</span>
              </div>
            )}
          </div>
        </details>
      )}

      {/* AI Analysis */}
      {visitor.analysis && (
        <div className="mkt-profile-section">
          <h3>AI Trademark Analysis</h3>
          <div className="mkt-profile-grid">
            {visitor.analysis.riskLevel && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Risk Level</span>
                <span className={`mkt-field-value mkt-risk-badge mkt-risk-${visitor.analysis.riskLevel.toLowerCase()}`}>
                  {visitor.analysis.riskLevel}
                </span>
              </div>
            )}
            {visitor.analysis.registrationLikelihood && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Registration Likelihood</span>
                <span className="mkt-field-value">{visitor.analysis.registrationLikelihood}</span>
              </div>
            )}
            {visitor.analysis.distinctivenessScore != null && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Distinctiveness</span>
                <span className="mkt-field-value">{visitor.analysis.distinctivenessScore}/100 ({visitor.analysis.distinctivenessRating || ''})</span>
              </div>
            )}
            {visitor.analysis.estimatedTimeline && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Est. Timeline</span>
                <span className="mkt-field-value">{visitor.analysis.estimatedTimeline}</span>
              </div>
            )}
            {visitor.analysis.summary && (
              <div className="mkt-profile-field mkt-field-wide">
                <span className="mkt-field-label">AI Summary</span>
                <span className="mkt-field-value mkt-analysis-text">{visitor.analysis.summary}</span>
              </div>
            )}
            {visitor.analysis.recommendation && (
              <div className="mkt-profile-field mkt-field-wide">
                <span className="mkt-field-label">Recommendation</span>
                <span className="mkt-field-value mkt-analysis-text">{visitor.analysis.recommendation}</span>
              </div>
            )}
          </div>

          {/* Conflicts */}
          {visitor.analysis.conflicts?.length > 0 && (
            <div className="mkt-analysis-subsection">
              <h4>Potential Conflicts ({visitor.analysis.conflicts.length})</h4>
              <div className="mkt-conflicts-list">
                {visitor.analysis.conflicts.map((c, i) => (
                  <div key={i} className="mkt-conflict-item">
                    <span className={`mkt-conflict-risk mkt-risk-${c.risk}`}>{c.risk}</span>
                    <span className="mkt-conflict-name">{c.name}</span>
                    {c.class && <span className="mkt-conflict-class">Class {c.class}</span>}
                    {c.jurisdiction && <span className="mkt-conflict-jurisdiction">{c.jurisdiction}</span>}
                    {c.notes && <div className="mkt-conflict-notes">{c.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Classes */}
          {visitor.analysis.recommendedClasses?.length > 0 && (
            <div className="mkt-analysis-subsection">
              <h4>Recommended Classes</h4>
              <div className="mkt-classes-list">
                {visitor.analysis.recommendedClasses.map((cls, i) => (
                  <div key={i} className="mkt-class-item">
                    <span className="mkt-class-number">Class {cls.classNumber || cls.class}</span>
                    <span className="mkt-class-name">{cls.className || cls.name}</span>
                    {cls.relevance && <span className="mkt-class-relevance">{cls.relevance}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Website Scan Result (from visitor_tracking) */}
      {visitor.aiScanResult && typeof visitor.aiScanResult === 'object' && (
        <div className="mkt-profile-section">
          <h3>Website Scan (AI)</h3>
          <div className="mkt-profile-grid">
            {visitor.aiScanResult.businessName && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Detected Business</span>
                <span className="mkt-field-value">{visitor.aiScanResult.businessName}</span>
              </div>
            )}
            {visitor.aiScanResult.industry && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Detected Industry</span>
                <span className="mkt-field-value">{visitor.aiScanResult.industry}</span>
              </div>
            )}
            {visitor.aiScanResult.products && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Products/Services</span>
                <span className="mkt-field-value">{visitor.aiScanResult.products}</span>
              </div>
            )}
            {visitor.aiScanResult.suggestedTrademarkUse && (
              <div className="mkt-profile-field">
                <span className="mkt-field-label">Suggested Use</span>
                <span className="mkt-field-value">{visitor.aiScanResult.suggestedTrademarkUse}</span>
              </div>
            )}
            {visitor.aiScanResult.businessDescription && (
              <div className="mkt-profile-field mkt-field-wide">
                <span className="mkt-field-label">AI Description</span>
                <span className="mkt-field-value mkt-analysis-text">{visitor.aiScanResult.businessDescription}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {visitor.aiScanResult && typeof visitor.aiScanResult === 'string' && (
        <div className="mkt-profile-section">
          <h3>Website Scan (AI)</h3>
          <div className="mkt-profile-grid">
            <div className="mkt-profile-field mkt-field-wide">
              <span className="mkt-field-label">Result</span>
              <span className="mkt-field-value mkt-analysis-text">{visitor.aiScanResult}</span>
            </div>
          </div>
        </div>
      )}

      {/* Outreach History */}
      {visitor.outreachLog?.length > 0 && (
        <div className="mkt-profile-section">
          <h3>Outreach History</h3>
          <div className="mkt-outreach-log">
            {visitor.outreachLog.map((entry, i) => (
              <div key={entry.id || i} className="mkt-outreach-entry">
                <div className="mkt-outreach-header">
                  <span className="mkt-outreach-type">{entry.type?.replace(/_/g, ' ')}</span>
                  <span className="mkt-outreach-date">{formatDate(entry.sentAt)}</span>
                </div>
                <div className="mkt-outreach-details">
                  <span>To: {entry.recipientEmail}</span>
                  {entry.sentByName && <span> · By: {entry.sentByName}</span>}
                  {entry.subject && <div className="mkt-outreach-subject">Subject: {entry.subject}</div>}
                  {entry.customMessage && <div className="mkt-outreach-custom">"{entry.customMessage}"</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Dates */}
      <div className="mkt-profile-section">
        <h3>Key Dates</h3>
        <div className="mkt-profile-grid">
          <div className="mkt-profile-field">
            <span className="mkt-field-label">First Seen</span>
            <span className="mkt-field-value">{formatDate(visitor.createdAt)}</span>
          </div>
          {visitor.updatedAt && visitor.updatedAt !== visitor.createdAt && (
            <div className="mkt-profile-field">
              <span className="mkt-field-label">Last Activity</span>
              <span className="mkt-field-value">{formatDate(visitor.updatedAt)}</span>
            </div>
          )}
          {visitor.contactCapturedAt && (
            <div className="mkt-profile-field">
              <span className="mkt-field-label">Contact Captured</span>
              <span className="mkt-field-value">{formatDate(visitor.contactCapturedAt)}</span>
            </div>
          )}
          {visitor.paidAt && (
            <div className="mkt-profile-field">
              <span className="mkt-field-label">Paid</span>
              <span className="mkt-field-value">{formatDate(visitor.paidAt)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="mkt-profile-section">
        <h3>Notes</h3>
        <div className="mkt-note-form">
          <textarea
            className="mkt-textarea"
            placeholder="Add a note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={2}
          />
          <button
            onClick={handleAddNote}
            className="mkt-btn mkt-btn-secondary"
            disabled={!noteText.trim() || addNote.isPending}
          >
            {addNote.isPending ? 'Adding...' : 'Add Note'}
          </button>
        </div>
        {visitor.notes?.length > 0 && (
          <div className="mkt-notes-list">
            {visitor.notes.map((note) => (
              <div key={note.id} className="mkt-note">
                <div className="mkt-note-header">
                  <span className="mkt-note-author">{note.authorName}</span>
                  <span className="mkt-note-time">{formatDate(note.createdAt)}</span>
                </div>
                <div className="mkt-note-content">{note.content}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="mkt-profile-section">
        <h3>Timeline</h3>
        <div className="mkt-timeline">
          {(visitor.timeline || []).map((event, idx) => (
            <div key={idx} className="mkt-timeline-event">
              <div className="mkt-timeline-icon">
                {TIMELINE_ICONS[event.type] || '•'}
              </div>
              <div className="mkt-timeline-body">
                <div className="mkt-timeline-title">{formatEventType(event.type)}</div>
                <div className="mkt-timeline-time">{formatDate(event.timestamp)}</div>
                {event.data && (
                  <div className="mkt-timeline-data">
                    {renderEventData(event)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Outreach compose modal (same composer as the pipeline board) */}
      <OutreachModal
        visitor={composeVisitor}
        isOpen={outreachOpen}
        onClose={() => setOutreachOpen(false)}
      />
    </div>
  );
}

function formatStage(stage) {
  const labels = {
    landing: 'Landed',
    start: 'Start Page',
    business: 'Business Info',
    analysis: 'Analysis & Contact',
    contact: 'Analysis & Contact',
    countries: 'Countries',
    details: 'Details',
    checkout: 'Checkout',
    quote: 'Quote Requested',
    paid: 'Paid',
    anonymous: 'Anonymous',
    dropped_off: 'Dropped Off',
  };
  return labels[stage] || stage;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatEventType(type) {
  const labels = {
    visitor_landed: 'Visitor Landed',
    tracking_captured: 'Tracking Captured',
    wizard_started: 'Wizard Started',
    step_start: 'Get Started Clicked',
    step_business: 'Business Step',
    step_analysis: 'Analysis Step',
    step_contact: 'Contact Captured',
    step_countries: 'Countries Step',
    step_details: 'Details Step',
    step_checkout: 'Checkout Step',
    step_quote: 'Quote Requested',
    analysis_triggered: 'Analysis Triggered',
    analysis_completed: 'Analysis Completed',
    contact_captured: 'Contact Captured',
    countries_selected: 'Countries Selected',
    payment_complete: 'Payment Complete',
    ai_cost: 'AI Usage',
    note_added: 'Note Added',
    follow_up_sent: 'Follow-up Sent',
    status_change: 'Status Changed',
  };
  return labels[type] || type;
}

function renderEventData(event) {
  const { type, data } = event;
  if (!data) return null;

  switch (type) {
    case 'visitor_landed':
      return (
        <span>
          {data.trafficSource && `Source: ${data.trafficSource}`}
          {data.landingPage && ` · Page: ${data.landingPage}`}
          {data.isReturning && ' · Returning'}
        </span>
      );
    case 'tracking_captured':
      return (
        <span>
          {data.source && `Source: ${data.source}`}
          {data.campaign && ` · Campaign: ${data.campaign}`}
          {data.landingPage && ` · Page: ${data.landingPage}`}
        </span>
      );
    case 'wizard_started':
      return data.trademarkName ? <span>Trademark: &ldquo;{data.trademarkName}&rdquo;</span> : null;
    case 'analysis_completed':
      return data.riskLevel ? <span>Risk: {data.riskLevel}</span> : null;
    case 'contact_captured':
      return <span>{data.name} · {data.email}</span>;
    case 'countries_selected':
      return (
        <span>
          {data.countries?.join(', ')}
          {data.total ? ` · $${data.total.toLocaleString()}` : ''}
        </span>
      );
    case 'payment_complete':
      return data.transactionValue ? <span>${data.transactionValue.toLocaleString()} {data.currency || ''}</span> : (data.amount ? <span>${data.amount.toLocaleString()}</span> : null);
    case 'ai_cost':
      return <span>{data.type} · {data.model} · ${(data.cost || 0).toFixed(3)}</span>;
    case 'note_added':
      return <span>{data.content}</span>;
    default:
      return null;
  }
}

export default VisitorProfile;
